import { OutlookAttachment, OutlookAttachmentFormat } from '../client/types/CreateEmailObjectRequest';
import { BodyCoersionType } from './OfficeTypes';

type PropertyGetter<T> = {
  getAsync?: (callback: (result: Office.AsyncResult<T>) => void) => void;
} & T;

const getAttachmentContent = async (attachmentId: string): Promise<Office.AttachmentContent> => {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.getAttachmentContentAsync(attachmentId, {}, (result: Office.AsyncResult<Office.AttachmentContent>) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(result.error?.message || 'Failed to get attachment content'));
        return;
      }
      resolve(result.value);
    });
  });
};

const getAsyncProperty = <T>(property: PropertyGetter<T>, defaultValue: T): Promise<T> => {
  return new Promise((resolve, reject) => {
    if (property.getAsync) {
      property.getAsync((res) => {
        if (res.status === Office.AsyncResultStatus.Succeeded) {
          resolve(res.value ?? defaultValue);
        } else {
          reject(res.error);
        }
      });
    } else {
      resolve((property as T) ?? defaultValue);
    }
  });
};

export const OfficeApi = {
  getBody: (type: BodyCoersionType = BodyCoersionType.Html): Promise<string> => {
    const officeCoercionType = {
      [BodyCoersionType.Html]: Office.CoercionType.Html,
      [BodyCoersionType.Text]: Office.CoercionType.Text,
    }[type];

    return new Promise((resolve, reject) => {
      Office.context.mailbox.item.body.getAsync(officeCoercionType, {}, (res) => {
        if (res.status !== Office.AsyncResultStatus.Succeeded) {
          reject(res.error);
          return;
        }
        resolve(res.value);
      });
    });
  },

  getSubject: (): Promise<string> => {
    const item = Office.context.mailbox.item;
    return getAsyncProperty(item.subject, '');
  },

  getCC: (): Promise<Office.EmailAddressDetails[]> => {
    const item = Office.context.mailbox.item;
    return getAsyncProperty(item.cc, []);
  },

  getBCC: (): Promise<Office.EmailAddressDetails[]> => {
    return new Promise((resolve, reject) => {
      const item = Office.context.mailbox.item;
      if (item.bcc.getAsync) {
        item.bcc.getAsync((res) => {
          if (res.status === Office.AsyncResultStatus.Succeeded) {
            resolve(res.value ?? []);
          } else {
            reject(res.error);
          }
        });
      } else {
        resolve(Array.isArray(item.bcc) ? item.bcc : []);
      }
    });
  },

  getTo: (): Promise<Office.EmailAddressDetails[]> => {
    const item = Office.context.mailbox.item;
    return getAsyncProperty(item.to, []);
  },

  getFrom: (): Promise<Office.EmailAddressDetails> => {
    const item = Office.context.mailbox.item;
    return getAsyncProperty(item.from, null);
  },

  isReadSurface: (): boolean => {
    return Office.context.mailbox.item?.displayReplyForm != undefined;
  },

  subscribeToRecipientsChanged: (callback: (event: Office.RecipientsChangedEventArgs) => void): void => {
    Office.context.mailbox.item.addHandlerAsync(Office.EventType.RecipientsChanged, callback);
  },

  subscribeToItemChanged: (callback: () => void) => {
    Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, callback);
  },

  subscribeToAttachmentsChanged: (callback: () => void) => {
    Office.context.mailbox.item.addHandlerAsync(Office.EventType.AttachmentsChanged, callback);
  },

  unsubscribeFromAttachmentsChanged: () => {
    Office.context.mailbox.item.removeHandlerAsync(Office.EventType.AttachmentsChanged);
  },

  unsubscribeFromRecipientsChanged: (): void => {
    Office.context.mailbox.item.removeHandlerAsync(Office.EventType.RecipientsChanged);
  },

  unsubscribeFromItemChanged: () => {
    Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged);
  },

  getAttachments: async (): Promise<OutlookAttachment[]> => {
    try {
      // Get attachments list using either async or sync method
      const attachmentsList = await (async (): Promise<Office.AttachmentDetailsCompose[] | Office.AttachmentDetails[]> => {
        if (Office.context.mailbox.item.getAttachmentsAsync) {
          return new Promise((resolve, reject) => {
            Office.context.mailbox.item.getAttachmentsAsync((result) => {
              if (result.status !== Office.AsyncResultStatus.Succeeded) {
                reject(new Error(result.error?.message || 'Failed to get attachments'));
                return;
              }
              resolve(result.value);
            });
          });
        } else {
          // Convert sync attachments to same format as async result
          return Office.context.mailbox.item.attachments || [];
        }
      })();

      // Fetch content for all attachments in parallel
      const attachmentsWithContent = await Promise.all(
        attachmentsList.map(async (attachment) => {
          try {
            const content = await getAttachmentContent(attachment.id);
            return { attachment, content };
          } catch (error) {
            console.error(`Failed to get content for attachment ${attachment.name}:`, error);
            throw error; // Re-throw since we want content in all cases
          }
        }),
      );

      return attachmentsWithContent.map(({ attachment, content }) => ({
        id: attachment.id,
        content: content.content,
        format: formatToEnum(content.format),
        fileName: attachment.name,
        size: attachment.size,
        isInline: attachment.isInline,
        contentType: attachment.contentType,
      }));
    } catch (error) {
      console.error('Error getting attachments:', error);
      throw error;
    }
  },
  getItemId: (): Promise<string> => {
    const item = Office.context.mailbox.item;
    return new Promise((resolve, reject) => {
      if (item.getItemIdAsync) {
        item.getItemIdAsync({}, (res) => {
          if (res.status !== Office.AsyncResultStatus.Succeeded) {
            reject(res.error);
          }
          resolve(res.value);
        });
      }
      resolve(item.itemId);
    });
  },
};

const formatToEnum = (format: Office.MailboxEnums.AttachmentContentFormat | string): OutlookAttachmentFormat => {
  const map = {
    [Office.MailboxEnums.AttachmentContentFormat.Base64]: OutlookAttachmentFormat.Base64,
    [Office.MailboxEnums.AttachmentContentFormat.Eml]: OutlookAttachmentFormat.Eml,
    [Office.MailboxEnums.AttachmentContentFormat.ICalendar]: OutlookAttachmentFormat.ICalendar,
    [Office.MailboxEnums.AttachmentContentFormat.Url]: OutlookAttachmentFormat.Url,
  };

  return map[format] ?? OutlookAttachmentFormat.Base64;
};
